import { Prisma } from "@prisma/client";
import { PLATFORMS } from "@coso/core/constants";
import { z } from "zod";

// JSON
//------------------------------------------------------

export const transformJsonNull = (v?: any) => {
  if (!v || v === "DbNull") return Prisma.DbNull;
  if (v === "JsonNull") return Prisma.JsonNull;
  return v;
};

export const JsonValueSchema: z.ZodType<Prisma.JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.literal(null),
    z.record(z.lazy(() => JsonValueSchema.optional())),
    z.array(z.lazy(() => JsonValueSchema)),
  ])
);

export type JsonValueType = z.infer<typeof JsonValueSchema>;

export const NullableJsonValue = z
  .union([JsonValueSchema, z.literal("DbNull"), z.literal("JsonNull")])
  .nullable()
  .transform((v) => transformJsonNull(v));

export type NullableJsonValueType = z.infer<typeof NullableJsonValue>;

export const InputJsonValueSchema: z.ZodType<Prisma.InputJsonValue> = z.lazy(
  () =>
    z.union([
      z.string(),
      z.number(),
      z.boolean(),
      z.object({ toJSON: z.function(z.tuple([]), z.any()) }),
      z.record(z.lazy(() => z.union([InputJsonValueSchema, z.literal(null)]))),
      z.array(z.lazy(() => z.union([InputJsonValueSchema, z.literal(null)]))),
    ])
);

export type InputJsonValueType = z.infer<typeof InputJsonValueSchema>;

// @ts-expect-error: platforms is a valid enum
export const SocialPlatformSchema = z.enum(PLATFORMS);

export type SocialPlatformType = z.infer<typeof SocialPlatformSchema>;

export const RoleTypeSchema = z.enum([
  "admin",
  "editor",
  "readonly",
  "account_manager",
]);

export type RoleTypeType = z.infer<typeof RoleTypeSchema>;

/// Models
export const UserSchema = z.object({
  id: z.string().cuid(),
  email: z.string(),
  name: z.string().nullable(),
  image: z.string().nullable(),
  emailVerified: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  schedulerId: z.string().nullable(),
  companyWebsite: z.string().nullable(),
  paid: z.boolean(),
  isGod: z.boolean(),
});

export type User = z.infer<typeof UserSchema>;

export const UserUpdateSchema = z
  .object({
    email: z.string().optional(),
    name: z.string().nullable().optional(),
    image: z.string().nullable().optional(),
    emailVerified: z.coerce.date().nullable().optional(),
    createdAt: z.coerce.date().optional(),
    schedulerId: z.string().nullable().optional(),
    companyWebsite: z.string().nullable().optional(),
    paid: z.boolean().optional(),
    isGod: z.boolean().optional(),
  })
  .strict();

export type UserUpdate = z.infer<typeof UserUpdateSchema>;

export const RoleSchema = z.object({
  id: z.string(),
  type: RoleTypeSchema,
  brandId: z.string(),
  userId: z.string(),
  user: UserSchema.optional(),

  weekly_emails: z.coerce.boolean(),
});

export const RoleUpdateSchema = z.object({
  id: z.string().optional(),
  type: RoleTypeSchema.optional(),
  brandId: z.string(),
  userId: z.string(),
  weekly_emails: z.coerce.boolean(),
});

export const OrganisationSchema = z.object({
  id: z.string(),
  name: z.string(),
  paid: z.boolean(),
});

export type Organisation = z.infer<typeof OrganisationSchema>;

export const OrganisationUpdateSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  paid: z.coerce.boolean().optional(),
});

export type OrganisationUpdate = z.infer<typeof OrganisationUpdateSchema>;

export const BrandSettingsSchema = z.object({
  id: z.string(),
  brandId: z.string().nullable(),
  description: z.string(),
  businessType: z.string().min(1).optional(),
  industry: z.string().min(1).optional(),
  targetGeo: z.string().min(1).optional(),
  targetAudience: z.string().min(1).optional(),
  location: z
    .object({
      address: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      zip: z.string().optional(),
      country: z.string().optional(),
    })
    .nullable()
    .optional(),
  toneOfVoice: z.string().min(1).optional(),
  systemPrompt: z.string(),
  defaultPrompt: z.string(),
  colors: z.string().array(),
  inspirationPosts: z.string().array(),
  imageTemplates: z.string().array(),
  defaultImageTemplate: z.string().nullable(),
});

export type BrandSettings = z.infer<typeof BrandSettingsSchema>;

export const BrandSettingsUpdateSchema = z
  .object({
    description: z.string().optional(),
    businessType: z.string().nullable().optional(),
    industry: z.string().nullable().optional(),
    targetGeo: z.string().nullable().optional(),
    targetAudience: z.string().nullable().optional(),
    location: z
      .object({
        address: z.string().optional(),
        city: z.string().optional(),
        state: z.string().optional(),
        zip: z.string().optional(),
        country: z.string().optional(),
      })
      .nullable()
      .optional(),
    language: z.string().optional(),
    toneOfVoice: z.string().nullable().optional(),
    systemPrompt: z.string().optional(),
    defaultPrompt: z.string().optional(),
    colour_palette: z
      .object({
        primary: z.string().optional(),
        secondary: z.string().optional(),
        accent: z.string().optional(),
        background: z.string().optional(),
        text: z.string().optional(),
        border: z.string().optional(),
        shadow: z.string().optional(),
        hover: z.string().optional(),
        active: z.string().optional(),
        disabled: z.string().optional(),
        focus: z.string().optional(),
      })
      .optional(),
    colors: z.string().array().optional(),
    postLengthPreference: z.string().optional(),
    narrativeStyle: z.string().optional(),
    inspirationPosts: z.string().array().optional(),
    imageTemplates: z.string().array().optional(),
    defaultImageTemplate: z.string().nullable().optional(),
  })
  .strict();

export type BrandSettingsUpdate = z.infer<typeof BrandSettingsUpdateSchema>;

export const BrandSchema = z.object({
  id: z.string(),
  organisationId: z.string(),
  name: z.string(),
  logo: z.string().nullable(),
  settings: BrandSettingsSchema.optional(),
  roles: RoleSchema.array().optional(),
});

export type Brand = z.infer<typeof BrandSchema>;

export const BrandUpdateSchema = z.object({
  id: z.string().nullable().optional(),
  name: z.string().min(1).optional(),
  logo: z.string().nullable().optional(),
  websiteUrl: z.string().nullable().optional(),
  settings: BrandSettingsUpdateSchema.optional(),
});

export type BrandUpdate = z.infer<typeof BrandUpdateSchema>;

export const BrandCreateSchema = z.object({
  id: z.string().cuid().optional(),
  organisationId: z.string(),
  name: z.string().min(1),
  logo: z.string().nullable().optional(),
  websiteUrl: z.string().optional(),
  settings: BrandSettingsUpdateSchema.optional(),
});

export type BrandCreate = z.infer<typeof BrandCreateSchema>;

export const TaskParentTypeEnumSchema = z.enum([
  "trigger",
  "contentsource",
  "text",
  "media",
  "post",
  "action",
]);

export type TaskParentTypeEnum = z.infer<typeof TaskParentTypeEnumSchema>;

const TaskTypeConfigBase = z.object({
  name: z.string(),
  placeholder: z.string().optional(),
  description: z.string().optional(),
  default: z.any().optional(),
  required: z.boolean().optional(),
  requires: z.string().optional(),
  advanced: z.boolean().optional(),
});

export const TaskTypeConfigSchema = z.discriminatedUnion("type", [
  TaskTypeConfigBase.extend({
    type: z.literal("text"),
    default: z.string().optional(),
  }),
  TaskTypeConfigBase.extend({
    type: z.literal("image_template"),
    filter: z.any().optional(),
    default: z
      .object({
        rendered: z.string().optional(),
        template: z.any(),
      })
      .optional(),
  }),
  TaskTypeConfigBase.extend({
    type: z.literal("text_template"),
    filter: z.any().optional(),
    default: z.string().optional(),
  }),
  TaskTypeConfigBase.extend({
    type: z.literal("user"),
    default: z.string().optional(),
  }),
  TaskTypeConfigBase.extend({
    type: z.literal("color"),
    default: z.string().optional(),
  }),
  TaskTypeConfigBase.extend({
    type: z.literal("percentage"),
    default: z.number().optional(),
  }),
  TaskTypeConfigBase.extend({
    type: z.literal("number"),
    default: z.number({ description: "number as string" }).optional(),
  }),
  TaskTypeConfigBase.extend({
    type: z.literal("textarea"),
    default: z.string().optional(),
  }),
  TaskTypeConfigBase.extend({
    type: z.literal("url"),
    default: z.string().optional(),
  }),
  TaskTypeConfigBase.extend({
    type: z.literal("boolean"),
    default: z.boolean().optional(),
  }),
  TaskTypeConfigBase.extend({
    type: z.literal("select"),
    default: z.string().optional(),
    options: z.string().array(),
  }),
  TaskTypeConfigBase.extend({
    type: z.literal("dataset"),
    datatype: z.enum(["string", "url", "json"]),
    filter: z.any().optional(),
    default: z.string().array().optional(),
  }),
  TaskTypeConfigBase.extend({
    type: z.literal("collection"),
    filter: z.any().optional(),
    accepts: z.string().array().optional(),
  }),
  TaskTypeConfigBase.extend({
    type: z.literal("time"),
    default: z.object({ hour: z.number(), minute: z.number() }),
  }),
  TaskTypeConfigBase.extend({
    type: z.literal("schedule"),
    default: z.object({
      repeat: z.enum(["daily", "weekly", "monthly"]),
      day: z
        .enum([
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
          "saturday",
          "sunday",
        ])
        .optional(),
      date: z.number().optional(),
      time: z.object({ hour: z.number(), minute: z.number() }),
    }),
  }),
  TaskTypeConfigBase.extend({
    type: z.literal("platforms"),
    default: z.string().optional(),
  }),
  TaskTypeConfigBase.extend({
    type: z.literal("outputs"),
    default: z.string().array().optional(),
  }),
]);

export type TaskTypeConfig = z.infer<typeof TaskTypeConfigSchema>;

export const TaskOutput = z.object({
  url: z.string().url().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  text: z.string().optional(),
  image_prompt: z.string().optional(),
  images: z.string().array().optional(),
  platforms: SocialPlatformSchema.array().optional(),
  image_template: z.any().optional(),
  time: z.object({ hour: z.number(), minute: z.number() }).optional(),
  datetime: z.coerce.date().optional(),
  memory: z.record(z.string()).default({}).optional(),
});

export type TaskOutput = z.infer<typeof TaskOutput>;

export const TaskTypeSchema = z.object({
  id: z.string().cuid(),
  icon: z.string(),
  description: z.string(),
  parentType: TaskParentTypeEnumSchema,
  subtype: z.string(),
  colour: z.string().optional(),
  // @ts-expect-error:prisma
  outputs: z.enum(Object.keys(TaskOutput.shape)).array(),
  // @ts-expect-error:prisma
  inputs: z.enum(Object.keys(TaskOutput.shape)).array().optional(),
  configs: TaskTypeConfigSchema.array(),
});

export const TaskTypeUpdateSchema = z
  .object({
    icon: z.string().optional(),
    description: z.string().optional(),
    parentType: TaskParentTypeEnumSchema.optional(),
    configs: TaskTypeConfigSchema.optional(),
    defaultConfig: JsonValueSchema.optional(),
  })
  .strict();

export const TaskStatusSchema = z.enum([
  "active",
  "inactive",
  "busy",
  "idle",
  "disabled",
  "failed",
]);
export type TaskStatus = z.infer<typeof TaskStatusSchema>;

const TaskBaseSchema = z.object({
  id: z.string().cuid(),
  status: TaskStatusSchema,
  position: z.object({ x: z.number(), y: z.number() }),
  workflowId: z.string(),
  name: z.string(),
  typeId: z.string(),
  type: TaskTypeSchema,
  config: z.any().nullable(),
});

export const TaskSchema = TaskBaseSchema.extend({
  inputs: TaskBaseSchema.array().optional(),
  outputs: TaskBaseSchema.array().optional(),
});

export type Task = z.infer<typeof TaskSchema>;

export const MediaSchema = z.object({
  key: z.string(),
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  url: z.string().nullable().optional(),
  type: z.enum(["image", "video", "audio", "file"]),
  subtype: z.string().nullable().optional(),
  size: z.number().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  meta: JsonValueSchema.nullable(),
});

export type Media = z.infer<typeof MediaSchema>;

export const PostSchema = z.object({
  id: z.string().cuid(),
  platform: SocialPlatformSchema.array(),
  brandId: z.string(),
  time: z.coerce.date(),
  type: z.string().nullable().optional(),
  attachments: z.array(
    z
      .object({
        id: z.string(),
        mediaKey: z.string(),
        postId: z.string(),
        edits: z.any(),
        rendered: z.string().nullable().optional(),
        media: MediaSchema,
        loading: z.boolean().optional(),
      })
  ),
  caption: z.string(),
  urls: JsonValueSchema.array().nullable().optional(),
  userCreated: z.boolean().optional(),
  ayrshareId: z.string().nullable().optional(),
  idempotencyKey: z.string().optional(),
  status: z.enum(["pending", "scheduled", "draft", "error", "success", "discarded", "external"]).nullable().optional(),
  errors: JsonValueSchema.array().nullable().optional(),
});

export type Post = z.infer<typeof PostSchema>;

export const PostUpdateSchema = z
  .object({
    id: z.string().nullish().optional(),
    platform: SocialPlatformSchema.array().optional(),
    brandId: z.string().nullable().optional(),
    time: z.coerce.date().optional(),
    image: z.string().optional(),
    caption: z.string().optional(),
    urls: JsonValueSchema.array().nullable().optional(),
    userCreated: z.boolean().optional(),
    approved: z.boolean().optional(),
    ayrshareId: z.string().nullable().optional(),
    discarded: z.boolean().optional(),
    idempotencyKey: z.string().optional(),
    status: z
      .enum(["pending", "scheduled", "draft", "error", "success", "discarded", "external"])
      .nullable()
      .optional(),
    errors: JsonValueSchema.array().nullable().optional(),
    userId: z.string().nullable().optional(),
  })
  .strict();

export type PostUpdate = z.infer<typeof PostUpdateSchema>;
